import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import Chart from 'react-apexcharts';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

const MODE_PERIOD = 'period';
const MODE_CUMULATIVE = 'cumulative';

// Palette drawn from tailwind.config.js colours (no new tokens):
// blue.DEFAULT for the primary series + break-even marker, red.DEFAULT for the
// peak-funding-gap trough.
const BLUE = '#055DF5';
const RED = '#F00024';

const MODE_OPTIONS = [
    { id: MODE_PERIOD, label: $t('Period-net') },
    { id: MODE_CUMULATIVE, label: $t('Cumulative') },
];

const formatAxis = (value) => formatToFixedMoneyWithLocale(value);

// Cumulative cashflow chart for the org summary cashflow tab (IB-642 Phase 2).
// Period-net mode plots each month's net as a column; Cumulative mode plots the
// running cumulative as a line. Both annotate the break-even month (x-axis) and
// the peak-funding-gap trough (point), and force the zero line to stay visible
// for all-positive / all-negative series.
export default function OrganisationCashflowChart({ monthlyNet, cashflowKpis }) {
    const [mode, setMode] = useState(MODE_CUMULATIVE);

    const series = monthlyNet || [];

    const { options, chartSeries, chartType } = useMemo(() => buildChart(series, cashflowKpis, mode), [monthlyNet, cashflowKpis, mode]);

    if (series.length === 0) {
        return null;
    }

    const styles = `
    .apexcharts-toolbar {
      display: none;
    }
    `;

    return (
        <div className="tw-mt-6">
            <div className="tw-flex tw-items-center tw-justify-end tw-mb-3">
                <div className="tw-inline-flex tw-rounded-full tw-border-2 tw-border-blue tw-overflow-hidden" role="group">
                    {MODE_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            aria-pressed={mode === option.id}
                            onClick={() => setMode(option.id)}
                            className={classNames('tw-px-4 tw-py-1 tw-text-sm tw-font-medium tw-transition-colors', {
                                'tw-bg-blue tw-text-white': mode === option.id,
                                'tw-bg-white tw-text-blue': mode !== option.id,
                            })}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
            <Chart options={options} series={chartSeries} type={chartType} height={320} />
            <style nonce={window.nonceKey}>{styles}</style>
        </div>
    );
}

function buildChart(series, cashflowKpis, mode) {
    const labels = series.map((entry) => entry.label);
    const isCumulative = mode === MODE_CUMULATIVE;
    const valueKey = isCumulative ? 'cumulative' : 'net';
    const values = series.map((entry) => entry[valueKey]);

    return {
        chartType: isCumulative ? 'line' : 'bar',
        chartSeries: [
            {
                name: isCumulative ? $t('Cumulative') : $t('Period-net'),
                data: values,
            },
        ],
        options: {
            chart: {
                fontFamily: 'var(--font-sans)',
                type: isCumulative ? 'line' : 'bar',
                width: '100%',
                toolbar: { show: false },
                zoom: { enabled: false },
            },
            colors: [BLUE],
            stroke: { curve: 'smooth', width: isCumulative ? 3 : 0 },
            plotOptions: {
                bar: { columnWidth: '45%', borderRadius: 2 },
            },
            dataLabels: { enabled: false },
            xaxis: {
                categories: labels,
                tickPlacement: 'on',
            },
            yaxis: {
                // Keep the zero line on-chart even for all-positive or
                // all-negative series so the baseline is always readable.
                min: (min) => Math.min(0, min),
                max: (max) => Math.max(0, max),
                labels: { formatter: formatAxis },
            },
            tooltip: {
                y: { formatter: formatAxis },
            },
            annotations: buildAnnotations(series, cashflowKpis, isCumulative),
            grid: {
                // Emphasise the zero baseline.
                yaxis: { lines: { show: true } },
            },
        },
    };
}

// Annotate the break-even month (x-axis line) and the peak-funding-gap month
// (point on the cumulative trough). The break-even line is a time point and is
// shown in both modes. The peak-funding-gap point is a cumulative concept — in
// Period-net (bar) mode it would land on the trough month's net bar height,
// which isn't the gap depth — so it is only drawn in Cumulative mode, on the
// actual cumulative trough.
function buildAnnotations(series, cashflowKpis, isCumulative) {
    const xaxis = [];
    const points = [];

    if (!cashflowKpis) {
        return { xaxis, points };
    }

    const breakEvenEntry = cashflowKpis.break_even ? series.find((entry) => entry.key === cashflowKpis.break_even) : null;

    if (breakEvenEntry) {
        xaxis.push({
            x: breakEvenEntry.label,
            borderColor: BLUE,
            strokeDashArray: 4,
            label: {
                text: $t('Break-even'),
                borderColor: BLUE,
                style: { color: '#ffffff', background: BLUE },
            },
        });
    }

    // The peak funding gap is a signed trough; only annotate a genuine dip, and
    // only in Cumulative mode where the point sits on the real cumulative trough.
    if (isCumulative && cashflowKpis.peak_funding_gap < 0) {
        const troughEntry = series.reduce((deepest, entry) => (entry.cumulative < deepest.cumulative ? entry : deepest), series[0]);

        if (troughEntry) {
            points.push({
                x: troughEntry.label,
                y: troughEntry.cumulative,
                marker: { size: 5, fillColor: RED, strokeColor: RED },
                label: {
                    text: $t('Peak funding gap'),
                    borderColor: RED,
                    style: { color: '#ffffff', background: RED },
                },
            });
        }
    }

    return { xaxis, points };
}
